/**
 *
 * LinkItemSmall
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { setLeavingModal } from 'containers/Main/actions';
import NewTabLink from 'components/ClientResources/images/new-tab.svg';
import Image from 'components/ResourceItemSmall/components/Image';
import Title from 'components/ResourceItemSmall/components/Title';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import { getAlgoliaLocalizedField } from 'utils/localeUtils';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Mixpanel from 'utils/mixpanelService';
import { getClientLinkUrl } from 'utils/analyzeClientResource';

const useStyles = makeStyles(theme => ({
  wrapper: {
    position: 'relative',
  },
  link: {
    textDecoration: 'none',
    position: 'relative',
    width: '100%',
    display: 'inline',
  },
  container: {
    overflow: 'hidden',
    borderRadius: '5px !important',
    border: `1px solid #E9ECED`,
    height: 271,
    width: 'auto',
    position: 'relative',
    backgroundColor: '#FFFFFF',
  },
  clientExclusiveContainer: {
    borderColor: props => props.clientAccent || theme.palette.primary.main,
  },
  clientGroupExclusiveContainer: {
    borderColor: props =>
      props.clientGroupAccent ||
      props.clientAccent ||
      theme.palette.primary.main,
  },
  imageVideoContainer: {
    height: 123,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  clientExclusiveLabelWrapper: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    backgroundColor: props => props.clientAccent || theme.palette.primary.main,
    zIndex: 2,
  },
  clientGroupExclusiveLabelWrapper: {
    backgroundColor: props =>
      props.clientGroupAccent ||
      props.clientAccent ||
      theme.palette.primary.main,
  },
  clientExclusiveLabel: {
    ...theme.typography.overline,
    color: props => props.clientFontColor || '#FFFFFF',
    textAlign: 'center',
  },
  clientGroupExclusiveLabel: {
    color: props =>
      props.clientGroupFontColor || props.clientFontColor || '#FFFFFF',
  },
  contentWrapper: {
    position: 'relative',
    padding: '10px 10px 39px 10px',
    borderBottomRightRadius: 10,
    borderBottomLeftRadius: 10,
    zIndex: 1,
    backgroundColor: '#FFFFFF',
    height: 146,
  },
  infoWrapper: {
    maxHeight: '100%',
    overflow: 'hidden',
  },
  badgeAndIconWrapper: {
    position: 'relative',
    marginBottom: 4,
  },
  desktopWrapper: {
    zIndex: 0,
  },
  subtitleWrapper: {
    lineHeight: 1.14,
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: '2.9925rem' /* Fallback for non-webkit */,
  },
  subtitle: {
    display: 'block',
  },
  cta: {
    position: 'absolute',
    bottom: 12,
    left: 12,
    right: 12,
    display: 'flex',
    alignItems: 'center',
    height: '1.37375rem',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  ctaIcon: {
    height: 16,
    width: 16,
    marginRight: 8,
  },
}));

const LinkItemSmall = ({
  item,
  item: { title = '', article = '', slug, image, imageUrl = '' },
  listTitle,
  isPdf,
  grayscale,
  trackingPath,
  isClientExclusive,
  isClientGroupExclusive,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const clientDetails = useSelector(state => state.main.clientDetails);
  const [siteCopy] = useSiteCopySelector(['resource-item-page']);
  const locale = useAlgoliaLocale();

  const url = getClientLinkUrl(item, clientDetails);

  const classes = useStyles({
    clientAccent: _get(clientDetails, 'metadata.clientExclusiveAccent'),
    clientFontColor: _get(
      clientDetails,
      'metadata.clientExclusiveResource.fontColor',
    ),
    clientGroupAccent: _get(
      clientDetails,
      'clientGroup.metadata.clientExclusiveAccent',
    ),
    clientGroupFontColor: _get(
      clientDetails,
      'clientGroup.metadata.clientExclusiveResource.fontColor',
    ),
  });

  const redirect = () => {
    const pageName = location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track('Resource Card - Clicked', {
      name: title || article,
      type: title ? 'link' : 'source',
      page: finalPageName,
      path: trackingPath || location.pathname.split('/')[1],
      slug,
      listTitle,
      recommendation: undefined,
      subtype: undefined,
      blog: undefined,
      entryId: item.objectID || item.id,
    });
    dispatch(
      setLeavingModal({
        visible: true,
        title: getAlgoliaLocalizedField(item, 'title', locale),
        url,
      }),
    );
  };

  let file;
  if (!_isEmpty(imageUrl)) file = imageUrl;
  else if (_has(image, 'url')) file = image.url;
  else if (!_has(image, 'fields') && typeof image === 'object') file = {};
  else if (!_isEmpty(image) && typeof image === 'object')
    ({ file } = image.fields);
  else file = image;

  return (
    <div className={classes.wrapper}>
      <ButtonBase className={classes.link} onClick={redirect}>
        <Paper
          elevation={0}
          className={_classNames(classes.container, {
            [classes.clientExclusiveContainer]: isClientExclusive,
            [classes.clientGroupExclusiveContainer]: isClientGroupExclusive,
          })}
        >
          <div className={classes.desktopWrapper}>
            <Grid container>
              <Grid item xs={12} className={classes.imageVideoContainer}>
                <Image
                  file={file}
                  type="link"
                  isPdf={isPdf}
                  grayscale={grayscale}
                />
                {(isClientExclusive || isClientGroupExclusive) && (
                  <Box
                    className={_classNames(
                      classes.clientExclusiveLabelWrapper,
                      {
                        [classes.clientGroupExclusiveLabelWrapper]: isClientGroupExclusive,
                      },
                    )}
                  >
                    <Typography
                      className={_classNames(classes.clientExclusiveLabel, {
                        [classes.clientGroupExclusiveLabel]: isClientGroupExclusive,
                      })}
                    >
                      {_get(
                        clientDetails,
                        isClientGroupExclusive
                          ? 'clientGroup.metadata.clientExclusiveResource.label'
                          : 'metadata.clientExclusiveResource.label',
                        '',
                      )}
                    </Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} className={classes.contentWrapper}>
                <Grid container className={classes.infoWrapper}>
                  <Title
                    title={
                      getAlgoliaLocalizedField(item, 'title', locale) ||
                      getAlgoliaLocalizedField(item, 'article', locale)
                    }
                  />
                  <Grid item xs={12}>
                    <Typography
                      color="textPrimary"
                      align="left"
                      variant="overline"
                      className={classes.subtitleWrapper}
                    >
                      <span className={classes.subtitle}>
                        {getAlgoliaLocalizedField(item, 'subtitle', locale)}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      align="left"
                      color="textSecondary"
                      variant="overline"
                      className={classes.cta}
                    >
                      <img
                        src={NewTabLink}
                        alt=""
                        className={classes.ctaIcon}
                      />
                      {getAlgoliaLocalizedField(
                        item,
                        'callToAction',
                        locale,
                        _get(
                          siteCopy,
                          'pageCopy.clientExclusiveLearnMoreLabel',
                        ),
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
        </Paper>
      </ButtonBase>
    </div>
  );
};

LinkItemSmall.propTypes = {
  item: PropTypes.object,
  listTitle: PropTypes.string,
  grayscale: PropTypes.bool,
  isPdf: PropTypes.bool,
};

export default LinkItemSmall;
